import { useMutation, useQueryClient } from '@tanstack/react-query';
import { tradesService } from '@/services';
import { CreateTradeRequest, CreateTradeResponse } from '@/types';
import { queryKeys } from '@/services/query-keys';
import { cashPoolKeys } from '@/services/query-keys/cash-pool-keys';
import { logger } from '@/utils/logger';

type CreateTradeOptions = {
  onSuccess?: (data: CreateTradeResponse) => void;
  onError?: (error: Error) => void;
};

export type CreateTradeVariables = {
  data: CreateTradeRequest;
  /** One per fill of the booking form, sent with every resend of it. */
  idempotencyKey?: string;
};

export const useCreateTrade = (options?: CreateTradeOptions) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data, idempotencyKey }: CreateTradeVariables) =>
      tradesService.createTrade(data, idempotencyKey),
    onSuccess: (data) => {
      // LR-546: do NOT await a full-book refetch here. Awaiting kept the mutation
      // `isPending` (= the dialog's "Creating…" state) alive until the entire
      // position book reloaded; on a large book that refetch never finished, so
      // the operator re-submitted and created duplicate trades. `invalidateQueries`
      // marks the ['positions'] tree stale and TanStack background-refetches the
      // active (loaded) queries on its own — firing it without await lets the
      // modal close as soon as the POST returns 201.
      void queryClient.invalidateQueries({ queryKey: queryKeys.trades.all });
      // LR-586: booking puts `price + margin` into the chosen pool, so the
      // Collateral Manager balance must not serve its cached figure.
      void queryClient.invalidateQueries({ queryKey: cashPoolKeys.all });
      options?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      logger.error('Failed to create trade:', error);
      options?.onError?.(error);
    },
  });
};

